'use client';

import { useEffect, useRef } from 'react';

import { track, type EventName, type EventProps } from '@repo/analytics';

/**
 * Generic mounted-once page view tracker for surfaces that need a
 * semantic event beyond `$pageview`.
 *
 * Today, `$pageview` fires automatically via posthog-js autocapture for
 * every navigation. But every pageview looks the same — there's no way
 * for an analyst to filter "show me brand page views by brand_slug"
 * without manually parsing `properties.$pathname` with a LIKE pattern.
 *
 * This component fires a dedicated event with structured properties so
 * the analyst can:
 *   - Build a funnel like `brand_page_viewed → article_consumed → ...`
 *   - Group by `brand_slug` or `channel_handle` natively in PostHog
 *   - Compare brand-driven traffic to home-driven traffic without
 *     filtering on path strings
 *
 * Used by:
 *   - `apps/extension-webapp/src/app/(app)/brands/[slug]/page.tsx` →
 *     `brand_page_viewed` with `brand_slug`
 *   - `apps/extension-webapp/src/app/(app)/youtube/[channelHandle]/page.tsx` →
 *     `channel_page_viewed` with `channel_handle`
 *   - Future: `feed_page_viewed` for `/feeds/[slug]`
 *
 * AN-4a (2026-07-05): routed through the typed `track()` wrapper (doctrine
 * §6) — this file previously used raw `posthog.capture`, so its 66 fires
 * were invisible to registry tooling and slipped the Analytics Wrapper
 * Gate. The generic `<E extends EventName>` keeps the component reusable
 * while forcing every (event, properties) pair through the registry.
 * Event names + property shapes are byte-identical to the raw era — do
 * not rename either side, there is history on both events.
 *
 * Fires exactly once per mount. Re-fires on route change because Next.js
 * re-mounts the page component on slug change. Does NOT use sessionStorage
 * because the analyst wants to count REPEAT brand visits within a session
 * — these are explicit navigation actions, not bouncing-pageview noise.
 *
 * The event is also tagged via the existing super-properties from
 * CRE-461 (`surface=website`, `signed_in`, `landed_on_keypoint`, `kp_id`)
 * automatically — so segmenting by surface/auth/etc works for free.
 */

interface PageViewTrackerProps<E extends EventName> {
  event: E;
  properties: EventProps<E>;
}

export function PageViewTracker<E extends EventName>({
  event,
  properties,
}: PageViewTrackerProps<E>): null {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    track(event, properties);
    // We intentionally only fire once per mount; we ignore prop updates
    // because all the realistic update cases (e.g. brand data finishing
    // loading after a stub render) would double-fire and skew counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
